import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { activeHostMode } from "../../utils/hostMode";
import { useHistory } from "react-router";
import { ErrorWrapper } from "../../components/Error/Error";
import { modal } from "../../components/Modal/Modal";
import { ConfigContext } from "../../providers/ConfigProvider";
import { FF_UNSAVED_CHANGES, isFF } from "../../utils/feature-flags";
import { absoluteURL, removePrefix } from "../../utils/helpers";
import { clearScriptsCache, isScriptValid, reInsertScripts, replaceScript } from "../../utils/scripts";
import { UNBLOCK_HISTORY_MESSAGE } from "../App";

const pageCache = new Map();

// Browsers abort any in-flight fetch when the page starts navigating away
// (a link/logo click, or a back/forward gesture). That abort surfaces in
// loadAsyncPage's catch and used to pop an unrecoverable "Connection
// refused" modal (allowClose:false) on the page you were leaving — e.g.
// sign-in → logo → public home → Back. Track the unload state so we can
// tell a navigation-abort (ignore) from a genuine outage (show the modal).
let navigatingAway = false;
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    navigatingAway = true;
  });
  // bfcache restore (and normal show) — we're here again, not leaving.
  window.addEventListener("pageshow", () => {
    navigatingAway = false;
  });
}

const pageFromHTML = (html) => {
  const parser = new DOMParser();
  const document = parser.parseFromString(html, "text/html");
  return document;
};

const loadAsyncPage = async (url) => {
  try {
    if (pageCache.has(url)) {
      return pageCache.get(url);
    }
    const response = await fetch(url);
    const html = await response.text();

    if (response.status === 401) {
      // Public host modes (an extension directory on its own host) accept
      // anonymous visitors, and "/" is public there, so redirecting would
      // reload-loop. Leave it to the SPA.
      if (activeHostMode?.allowAnonymous) return;
      location.href = absoluteURL("/");
      return;
    }

    if (!response.ok) {
      // Desktop offline mode: the local proxy returns 503 with
      // ``{"error": "upstream unreachable: ..."}`` when Django is
      // dead. Don't pop a modal for it — the user can pick up where
      // they left off via the in-page "this page isn't available
      // offline" panel the proxy renders for HTML navigations, and
      // the SPA stays usable for cached pages.
      if (response.status === 503 && /upstream unreachable/.test(html)) {
        return null;
      }
      modal({
        body: () => (
          <ErrorWrapper
            title={`Error ${response.status}: ${response.statusText}`}
            errorId={response.status}
            stacktrace={`Cannot load url ${url}\n\n${html}`}
          />
        ),
        allowClose: false,
        style: { width: 680 },
      });
      return null;
    }

    pageCache.set(url, html);
    return html;
  } catch (err) {
    // A fetch aborted because the page is navigating away (back/forward,
    // logo/link click) isn't a real outage — don't trap the user behind
    // an unclosable "connection refused" modal on the way out.
    if (
      navigatingAway ||
      err?.name === "AbortError" ||
      (typeof document !== "undefined" && document.visibilityState === "hidden")
    ) {
      return null;
    }
    modal({
      body: () => (
        <ErrorWrapper
          possum={false}
          title={"Connection refused"}
          message={"Server not responding. Is it still running?"}
        />
      ),
      simple: true,
      allowClose: false,
      style: { width: 680 },
    });
    return null;
  }
};

/**
 * @param {HTMLElement} oldNode
 * @param {HTMLElement} newNode
 */
const swapNodes = async (oldNode, newNode) => {
  if (oldNode && newNode) {
    oldNode.replaceWith(newNode);
    await reInsertScripts(newNode);
  }
};

/**
 * @param {Document} oldPage
 * @param {Document} newPage
 */
const swapAppSettings = async (oldPage, newPage) => {
  const oldSettings = oldPage.querySelector("script#app-settings");
  const newSettings = newPage.querySelector("script#app-settings");

  if (oldSettings && newSettings) {
    await replaceScript(oldSettings, {
      sourceScript: newSettings,
      forceUpdate: true,
    });
  }
};

/**
 * @param {Document} oldPage
 * @param {Document} newPage
 */
const swapContent = async (oldPage, newPage) => {
  const currentContent = oldPage.querySelector("#dynamic-content");
  const newContent = newPage.querySelector("#dynamic-content");

  if (currentContent && newContent) {
    await swapNodes(currentContent, newContent);
  } else {
    await swapNodes(oldPage.body.children[0], newContent, { removeOld: false });
  }
};

/** @param {HTMLElement} nodes */
const nodesToSignatures = (nodes) => {
  return new Set(Array.from(nodes).map((n) => n.outerHTML));
};

/**
 * @param {HTMLHeadElement} oldHead
 * @param {HTMLHeadElement} newHead
 */
const swapHeadScripts = async (oldHead, newHead) => {
  swapNodes(oldHead.querySelector("title"), newHead.querySelector("title"));

  const fragment = document.createDocumentFragment();

  Array.from(newHead.querySelectorAll("script"))
    .filter((script) => isScriptValid(script))
    .forEach((script) => fragment.appendChild(script));

  Array.from(oldHead.querySelectorAll("script"))
    .filter((script) => isScriptValid(script))
    .forEach((script) => script.remove());

  oldHead.appendChild(fragment);
  await reInsertScripts(oldHead);
};

/**
 * @param {Document} oldPage
 * @param {Document} newPage
 */
const swapStylesheets = async (oldPage, newPage) => {
  const linkSelector = ["style:not([data-replaced])", "link[rel=stylesheet]:not([data-replaced])"].join(", ");
  const oldStyles = Array.from(oldPage.querySelectorAll(linkSelector));
  const newStyles = Array.from(newPage.querySelectorAll(linkSelector));

  const existingSignatures = nodesToSignatures(oldStyles);
  const stylesToReplace = newStyles.filter((style) => !existingSignatures.has(style.outerHTML));

  await Promise.all(
    stylesToReplace.map(
      (style) =>
        new Promise((resolve) => {
          style.onload = () => resolve(style.outerHTML);
          document.head.append(style);
        }),
    ),
  );
};

/** @param {Document} newPage */
const swapPageParts = async (newPage, onReady) => {
  document.title = newPage.title;

  await swapStylesheets(document, newPage);
  await swapHeadScripts(document.head, newPage.head);
  await swapAppSettings(document, newPage);
  await swapContent(document, newPage);
  onReady?.();
};

const isVisitable = (target) => {
  if (!target) return false;
  if (target.dataset.external) return false;
  if (target.getAttribute("href").match(/#/)) return false;
  if (target.origin !== location.origin) return false;

  return true;
};

const locationWithoutHash = () => {
  const { href } = location;
  return href.replace(/#(.*)/g, "");
};

const fetchPage = async (locationUrl) => {
  const html = await loadAsyncPage(locationUrl);
  return html ? pageFromHTML(html) : null;
};

let currentLocation = locationWithoutHash();

const useStaticContent = (initialContent, onContentLoad) => {
  const [staticContent, setStaticContent] = useState(initialContent);

  const fetchCallback = useCallback(async (locationUrl) => {
    currentLocation = locationUrl;
    clearScriptsCache();
    const result = await fetchPage(locationUrl);

    if (result) {
      await swapPageParts(result, onContentLoad);
      setStaticContent(result);
      return true;
    }
    return false;
  }, []);

  return [staticContent, fetchCallback];
};

export const AsyncPageContext = createContext(null);

export const AsyncPageConsumer = AsyncPageContext.Consumer;

export const AsyncPage = ({ children }) => {
  const initialContent = document;

  const history = useHistory();
  const config = useContext(ConfigContext);
  const onLoadCallback = useCallback(() => {
    config.update(window.APP_SETTINGS);
  }, []);
  const [staticContent, fetchStatic] = useStaticContent(initialContent, onLoadCallback);

  // Bundled SPA (Capacitor mobile) has no Django backend at the
  // WebView origin — every page is a React route and ``fetchStatic``
  // would hit the local file server, 404, and silently abort the
  // navigation. Skip the fetch and just push the route. Web/desktop
  // are unaffected because they keep going through the cloud Django.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCapacitor = typeof window.Capacitor !== "undefined";

  // Path prefixes that are 100% React-routed — Django doesn't have a
  // template for them, so ``fetchStatic`` would just fail and pop the
  // "Cannot load url" modal. Skip the fetch entirely and let
  // react-router handle the navigation. Add new SPA-only prefixes
  // here as we move more pages off Django templates; existing
  // Django-served pages (/projects, /workspaces root, /admin, etc.)
  // continue to fetch HTML so their server-side context still hydrates.
  const SPA_ONLY_PATH_PREFIXES = [
    "/organization/resources",
    "/organization/consent",
    "/organization/quality",
    "/organization/review",
    "/organization/activity",
    "/organization/analytics",
    "/organization/label-distribution",
    "/organization/settings",
  ];
  const isSpaOnlyPath = (pathname) =>
    SPA_ONLY_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  const onLinkClick = useCallback(async (e) => {
    /**@type {HTMLAnchorElement} */
    const target = e.target.closest("a[href]:not([target]):not([download])");

    if (!isVisitable(target)) return;
    if (target.matches("[data-external]")) return;
    if (e.metaKey || e.ctrlKey) return;

    e.preventDefault();

    if (onCapacitor || isSpaOnlyPath(target.pathname)) {
      history.push(`${removePrefix(target.pathname)}${target.search}`);
      return;
    }

    const fetched = await fetchStatic(target.href);

    if (fetched) {
      history.push(`${removePrefix(target.pathname)}${target.search}`);
    }
  }, []);

  const onPopState = useCallback(() => {
    // Prevent false positive triggers in case of blocking page transitions
    if (isFF(FF_UNSAVED_CHANGES) && history.isBlocking) return;
    const newLocation = locationWithoutHash();
    const isSameLocation = newLocation === currentLocation;

    if (!isSameLocation) {
      currentLocation = newLocation;
      if (!onCapacitor) fetchStatic(newLocation);
    }
  }, []);

  // Fallback in case of blocked transitions
  const onMessage = useCallback((event) => {
    if (event.origin !== window.origin) return;
    if (event.data?.source !== "label-studio") return;
    if (event.data?.payload !== UNBLOCK_HISTORY_MESSAGE) return;
    onPopState();
  }, []);

  // useEffect(onPopState, [location]);

  useEffect(() => {
    document.addEventListener("click", onLinkClick, { capture: true });
    window.addEventListener("popstate", onPopState);
    isFF(FF_UNSAVED_CHANGES) && window.addEventListener("message", onMessage);
    return () => {
      document.removeEventListener("click", onLinkClick, { capture: true });
      window.removeEventListener("popstate", onPopState);
      isFF(FF_UNSAVED_CHANGES) && window.removeEventListener("message", onMessage);
    };
  }, []);

  return <AsyncPageContext.Provider value={staticContent}>{children}</AsyncPageContext.Provider>;
};
